import React, { useCallback } from 'react';
import { toast } from 'sonner';

import {
  useCreateCompanionFromRowMutation,
  useSaveCompanionsMutation,
  useSyncCompanionIntakeMutation,
} from '@/services/api/intakeService';

import { Button } from '../../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import type { CompanionRow } from '../form/types';
import { CompanionRowItem } from './CompanionRowItem';

interface CompanionRowsSectionProps {
  intakeId: string;
  companions: CompanionRow[];
  onCompanionsChange: (companions: CompanionRow[]) => void;
  canEdit: boolean;
  isPrimary?: boolean;
  primaryIntakeId?: string | null;
}

export const CompanionRowsSection: React.FC<CompanionRowsSectionProps> = ({
  intakeId,
  companions = [],
  onCompanionsChange,
  canEdit,
  isPrimary = true,
  primaryIntakeId = null,
}) => {
  const [createCompanion, { isLoading: isCreating }] =
    useCreateCompanionFromRowMutation();
  const [syncCompanion, { isLoading: isSyncing }] =
    useSyncCompanionIntakeMutation();
  const [saveCompanions] = useSaveCompanionsMutation();

  const handleAddRow = useCallback(() => {
    onCompanionsChange([
      ...companions,
      {
        clientName: '',
        details: null,
        createdIntakeId: null,
      },
    ]);
  }, [companions, onCompanionsChange]);

  const handleRemoveRow = useCallback(
    (index: number) => {
      const row = companions[index];
      if (row.createdIntakeId) {
        toast.error('Cannot remove row after intake is created');
        return;
      }
      onCompanionsChange(companions.filter((_, i) => i !== index));
    },
    [companions, onCompanionsChange],
  );

  const handleFieldChange = useCallback(
    (index: number, field: keyof CompanionRow, value: string | boolean) => {
      const updated = companions.map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      );
      onCompanionsChange(updated);
    },
    [companions, onCompanionsChange],
  );

  const handleCreateIntake = useCallback(
    async (index: number) => {
      const row = companions[index];
      if (!row.clientName.trim()) {
        toast.error('Client name is required');
        return;
      }

      // Persist latest companions to backend to avoid relying on auto-save

      const backendCompanions = companions.map((r) => ({
        client_name: r.clientName,
        details: r.details ?? null,
        created_intake_id: r.createdIntakeId ?? null,
      }));
      await saveCompanions({
        id: intakeId,
        companions: backendCompanions,
      }).unwrap();

      const result = await createCompanion({
        intakeId,
        rowIndex: index,
      });

      if ('data' in result && result.data) {
        toast.success(`Created intake for ${row.clientName}`);
        // Optimistically set createdIntakeId locally to avoid waiting for refetch
        const updated = companions.map((r, i) =>
          i === index ? { ...r, createdIntakeId: result.data.id } : r,
        );
        onCompanionsChange(updated);
        // Open in new tab
        window.open(`/intakes/${result.data.id}`, '_blank');
      } else {
        toast.error('Failed to create companion intake');
      }
    },
    [companions, intakeId, createCompanion, saveCompanions, onCompanionsChange],
  );

  const handleSync = useCallback(
    async (targetIntakeId: string, clientName: string) => {
      const result = await syncCompanion({ intakeId, targetIntakeId });

      if ('data' in result) {
        toast.success(`Synced data to ${clientName}`);
      } else {
        toast.error('Failed to sync data');
      }
    },
    [intakeId, syncCompanion],
  );

  // Non-primary companion: show simple link to primary
  if (!isPrimary && primaryIntakeId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Family Members</CardTitle>
          <CardDescription>
            This intake is part of a companion case set.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700">
            Manage companion cases from the primary intake.{' '}
            <a
              href={`/intakes/${primaryIntakeId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline font-medium"
            >
              View Primary Intake →
            </a>
          </p>
        </CardContent>
      </Card>
    );
  }

  // Primary intake with no companions: hide if not editable
  if (!canEdit && companions.length === 0) {
    return null;
  }

  // Primary intake: show full companion management UI
  return (
    <Card>
      <CardHeader>
        <CardTitle>Family Members</CardTitle>
        <CardDescription>
          Add family members involved in the same incident. Each will become a
          separate intake with shared accident details. This intake is the
          primary for the group.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          {companions.map((row, index) => (
            <CompanionRowItem
              key={index}
              index={index}
              row={row}
              canEdit={canEdit}
              isCreating={isCreating}
              isSyncing={isSyncing}
              onFieldChange={handleFieldChange}
              onCreateIntake={handleCreateIntake}
              onRemoveRow={handleRemoveRow}
              onSync={handleSync}
            />
          ))}
        </div>

        {canEdit && (
          <Button variant="outline" onClick={handleAddRow}>
            + Add Family Member
          </Button>
        )}

        {!canEdit && (
          <p className="text-sm text-gray-600">
            Manage companions from the primary intake
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default CompanionRowsSection;
